import { ColDef, ICellRendererParams, ValueFormatterParams, ValueSetterParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { PauseLined, HourglassFullFilled, EditLined } from '@tag/tag-icons';

import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { ContractBillingStatusPascalCaseEnum, ContractStatusEnum } 
  from '@/graphql/contracts/_generated_/contracts';
import { CONTRACT_STATES_COLORS } from '@/config/statuses.config';
import { Pill } from '@/components/common/Pill/Pill';
import { ContractStatusFormatted } from '../../../../../ContractLines/grid/variableContractLinescolumns';
import { NumberUtils } from '@/utils/numbers.utils';
import { EnvUtils } from '@/utils/env.utils';

interface GetPauseContractLineColumnsProps {
  reasonCodes: ReasonCode[];
  trackEditedRow: (data: any, startDateChanged?: boolean) => void;
  getValidationErrors: (colDef: any, data: any) => string[];
  isDisableFutureLinePause?: boolean;
}

export const getPauseContractLineColumns = ({
  reasonCodes,
  trackEditedRow,
  getValidationErrors,
  isDisableFutureLinePause = false
}: GetPauseContractLineColumnsProps): ColDef<any>[] => {

  return [
    {
      colId: 'status',
      headerName: 'Status',
      field: 'status',
      autoHeight: true,
      maxWidth: 100,
      cellStyle: () => ({ justifyContent: 'center', display: 'flex' }),
      cellRenderer: ({ value }: ICellRendererParams) => {
        const status = value as ContractStatusEnum;
        const color = status && CONTRACT_STATES_COLORS[status];
        return (
        <Pill background={color} size="large">
          {ContractStatusFormatted[status]}
        </Pill>
        );
      },
    },
    {
      colId: 'pauseStatus',
      headerName: 'Pause Status',
      field: 'lineBillingStatus',
      autoHeight: true,
      maxWidth: 140,
      cellStyle: () => ({ justifyContent: 'center', display: 'flex' }),
      cellRenderer: ({ data }: ICellRendererParams) => {
        if (!data) return null;
      
        const status = data.lineBillingStatus;
      
        if (status === ContractBillingStatusPascalCaseEnum.Paused) {
          return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PauseLined />
          </div>
          );
        }
      
        if (status === ContractBillingStatusPascalCaseEnum.FuturePause) {
          return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HourglassFullFilled />
          </div>
          );
        }
      
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EditLined />
          </div>
        );
      },
    },
    {
      colId: 'billingType',
      headerName: 'Billing Type',
      field: 'billingType',
      initialWidth: 140,
    },
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'stockCode',
      initialWidth: 300,
      minWidth: 250,
      cellRenderer: ({ data }: ICellRendererParams) => {
        if (!data) return '';
        return `${data.stockCode || ''} - ${data.stockName || ''}`;
      },
    },
    {
      colId: 'quantity',
      headerName: 'Quantity',
      field: 'quantity',
      initialWidth: 110,
      minWidth: 110,
      cellStyle: () => ({ textAlign: 'right' }),
      valueFormatter: ({ value }: ValueFormatterParams) => {
        return NumberUtils.prettifyWithRound(value, 3, ',', 0) ?? '';
      },
    },
    {
      colId: 'unitPrice',
      headerName: 'Unit Price',
      field: 'unitPrice',
      initialWidth: 130,
      minWidth: 130,
      cellStyle: () => ({ textAlign: 'right' }),
      valueFormatter: ({ value }: ValueFormatterParams) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      },
    },
    {
      colId: 'reasonCode',
      headerName: 'Pause Reason',
      field: 'lineBillingStatusUpdateReasonCode',
      width: 180,
      editable: true,
      cellEditor: 'DropdownEditor',
      cellEditorParams: {
        options: reasonCodes.filter(rc => rc.enabled),
        getOptionValue: (option: ReasonCode) => option.code,
        getOptionLabel: (option: ReasonCode) => option.name,
        saveOnChange: true,
        isSearchable: true,
        formatOptionLabel: (option: ReasonCode) => (
          <div title={option.description ?? undefined}>{option.name}</div>
        ),
      },
      valueSetter: (params: ValueSetterParams) => {
        params.data.lineBillingStatusUpdateReasonCode = params.newValue?.code || params.newValue;
        trackEditedRow(params.data);
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: getValidationErrors(colDef, data),
      }),
      valueFormatter: ({ value }: ValueFormatterParams) => {
        const reasonCode = reasonCodes?.find(rc => rc.code === value);
        return reasonCode ? reasonCode.name : value || '-';
      },
    },
    {
      colId: 'startDate',
      headerName: 'Pause Start Date',
      field: 'lineBillingCurrentStatusStartDate',
      width: 160,
      editable: !isDisableFutureLinePause,
      cellEditor: 'DatepickerEditor',
      cellEditorParams: {
        saveOnChange: true,
      },
      valueSetter: (params: ValueSetterParams) => {
        params.data.lineBillingCurrentStatusStartDate = params.newValue;
        params.data.startDateHasChanged = true;
        trackEditedRow(params.data, params.data.startDateHasChanged);
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: getValidationErrors(colDef, data),
      }),
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return dayjs(value).format(DEFAULT_DATE_PATTERN);
        }
        return '-';
      },
    },
    {
      colId: 'endDate',
      headerName: 'Pause End Date',
      field: 'lineBillingCurrentStatusEndDate',
      width: 160,
      editable: true,
      cellEditor: 'DatepickerEditor',
      cellEditorParams: {
        saveOnChange: true,
      },
      valueSetter: (params: ValueSetterParams) => {
        params.data.lineBillingCurrentStatusEndDate = params.newValue;
        trackEditedRow(params.data);
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: getValidationErrors(colDef, data),
      }),
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return dayjs(value).format(DEFAULT_DATE_PATTERN);
        }
        return '-';
      },
    },
    ...(EnvUtils.isNextSoIssueDateEnabled() ? [{
      colId: 'nextSoIssueDate',
      headerName: 'Next SO Issue Date',
      field: 'nextSoIssueDate',
      initialWidth: 160,
      minWidth: 150,
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return dayjs(value).format(DEFAULT_DATE_PATTERN);
        }
        return '-';
      },
    }] : []),
    {
      colId: 'nextInvoiceDate',
      headerName: 'Next Invoice Date',
      field: 'nextInvoiceDate',
      initialWidth: 170,
      minWidth: 170,
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return dayjs(value).format(DEFAULT_DATE_PATTERN);
        }
        return '-';
      },
    },
    {
      colId: 'billingInterval',
      headerName: 'Billing Interval',
      field: 'billingInterval',
      initialWidth: 140,
      minWidth: 140,
      valueFormatter: ({ value }: ValueFormatterParams) => {
        return value || '-';
      },
    },
    {
      colId: 'reference',
      headerName: 'Reference',
      field: 'lineBillingStatusUpdateReference',
      width: 180,
      editable: true,
      valueSetter: (params: ValueSetterParams) => {
        const newValue = params.newValue?.trim();
        params.data.lineBillingStatusUpdateReference = newValue || null;
        trackEditedRow(params.data);
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: getValidationErrors(colDef, data),
      }),
      valueFormatter: ({ value }: ValueFormatterParams) => value || '-',
    },
    {
      colId: 'comment',
      headerName: 'Comment',
      field: 'lineBillingStatusUpdateComment',
      width: 250,
      editable: true,
      valueSetter: (params: ValueSetterParams) => {
        const newValue = params.newValue?.trim();
        params.data.lineBillingStatusUpdateComment = newValue || null;
        trackEditedRow(params.data);
        return true;
      },
      cellRenderer: 'CellRenderer',
      cellRendererParams: ({ colDef, data }: ICellRendererParams) => ({
        errors: getValidationErrors(colDef, data),
      }),
      valueFormatter: ({ value }: ValueFormatterParams) => value || '-',
    },
  ];
};
